//* Importing React and other modules
import React, { useState } from "react";

//* Importing the lucide-react icons
import { LogOut, Menu, X } from "lucide-react";

//* Importing the images
import logo from "../../../assets/images/logo-ontest.png";

//* Importing the styles
import SidebarHeader from "./SidebarHeader";

//* Importing the SidebarItem component
import SidebarItem from "../SideBar/SidebarItem";

//* Importing the styles
import styles from "./style.module.scss";

//* Importing the IconMapper
import { getIcon } from "../../common/IconMapper"; 

//* Importing the react-icons
import { MdMenu } from "react-icons/md"; 

//* Importing custom logout hook
import { useLogout } from "../../../hooks/useLogout";

/**
 * SideBar component renders a sidebar with dynamic menu items based on user permissions.
 * It includes a header, dynamically generated menu items, and a footer with a logout button.
 *
 * @component
 * @returns {JSX.Element} The rendered sidebar component.
 *
 * @example
 * // Example usage of SideBar component
 * import SideBar from './SideBar';
 * 
 * function App() {
 *   return (
 *     <div className="App">
 *       <SideBar />
 *     </div>
 *   );
 * }
 *
 * @description
 * The SideBar component uses the following hooks:
 * - `useSelector` to access the `me` object from the Redux store.
 * - `useDispatch` to dispatch actions to the Redux store.
 * - `useNavigate` to navigate to different routes.
 * - `useState` to manage the `shrunk` state for toggling the sidebar width.
 *
 * The component renders:
 * - A header with a toggle button to shrink/expand the sidebar.
 * - A dynamically generated menu based on user permissions.
 * - A footer with a logo and a logout button.
 *
 * @requires
 * - `react`
 * - `react-redux`
 * - `react-router-dom`
 * - `styles` for CSS styling.
 * - `getIcon` function to get the icon component for a feature or subFeature.
 * - `SidebarHeader` component for the sidebar header.
 * - `SidebarItem` component for individual menu items.
 * - `logout` action to log out the user.
 * - `logo` for the footer logo image.
 * - `LogOut` icon for the logout button.
 */
export default function SideBar() {
  //const { me } = useSelector((store) => store.auth);
  const logout = useLogout();

  const [shrunk, setShrunk] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
    if (window.innerWidth > 768) {
      setIsMobileOpen(false);
    }
  };


  React.useLayoutEffect(() => {
    checkMobile(); 
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);



  const handleShrunk = () => {
    setShrunk(!shrunk);
  };

  const handleMobileToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };
  
  const handleLogout = () => {
    logout();
  };

  const closeMobileSidebar = () => {
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Hamburger Button */}
      {isMobile && (
        <button
          className={styles.mobileMenuButton}
          onClick={handleMobileToggle}
          aria-label="Toggle menu"
        >
          {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobile && isMobileOpen && (
        <div
          className={styles.mobileOverlay}
          onClick={closeMobileSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`${styles.sidebar} ${isMobile ? styles.mobileSidebar : ''} ${
          isMobile && isMobileOpen ? styles.mobileOpen : ''
        } d-flex flex-column align-items-center hidethis ${isMobile ? styles.sidebarMobile : (shrunk ? styles.sidebarShrunk : styles.sidebarExpanded)}`}
      >
      <SidebarHeader shrunk={shrunk} handleShrunk={handleShrunk} isMobile={isMobile} />

      <hr className={styles.separator} />      
      <div className={styles.menuContainer}>

        <SidebarItem
          icon={React.createElement(getIcon("MdSchool") || MdMenu)}
          title="Processos Seletivos"
          href="/"
          shrunk={shrunk}
          onItemClick={closeMobileSidebar}
        />
      </div>

      <hr className={styles.separator} />

      {/* Footer */}
      <div className={`${styles.footer} d-flex flex-column align-items-center`}>
        <img src={logo} alt="Logo" className={styles.logo} />
        <span className={styles.copyright}>
          &copy; {new Date().getFullYear()}
          <span className={styles.logoutContainer}>
            <button
              onClick={handleLogout}
              className={`btn btn-danger btn-sm ${styles.logoutButton}`}
            >
              <LogOut size={16} /> {!shrunk && "Logout"}
            </button>
          </span>
        </span>
      </div>
      </div>
    </>
  );
}
